import { useLayoutEffect, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UserCheck, LogOut } from "lucide-react";
import { Button } from "@stockline/ui/components/button";
import { handleLogout } from "@/lib/auth-utils";
import {
  getImpersonatedEmail,
  isImpersonating,
  subscribeImpersonation,
} from "@/lib/impersonation";

/**
 * Persistent banner shown while a super admin is impersonating a user. Tracks
 * the impersonation marker reactively (useSyncExternalStore) so it disappears
 * the moment the marker is cleared — logout, a 401, or a fresh login can clear
 * it mid-session with no navigation. The admin token is not stashed during
 * impersonation, so "Exit" is a full logout — the admin signs back in to return
 * to the super-admin console.
 */
export default function ImpersonationBanner() {
  const queryClient = useQueryClient();
  const active = useSyncExternalStore(
    subscribeImpersonation,
    isImpersonating,
    () => false,
  );
  const email = useSyncExternalStore(
    subscribeImpersonation,
    getImpersonatedEmail,
    () => null,
  );

  // Reserve the banner's height at the top of the viewport via a body class
  // (--impersonation-banner-height in globals.css). The banner itself is fixed,
  // so the class is what lets full-height / top-anchored shells offset below it.
  // useLayoutEffect (not useEffect) so the reservation lands before the browser
  // paints — otherwise the shells render one frame at full height, then jump
  // down 36px once the class applies. No SSR here (Vite SPA), so it's safe.
  useLayoutEffect(() => {
    if (!active) return;
    document.body.classList.add("is-impersonating");
    return () => document.body.classList.remove("is-impersonating");
  }, [active]);

  if (!active) return null;

  const handleExit = () => handleLogout(queryClient);

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed inset-x-0 top-0 z-50 flex h-9 items-center justify-center gap-3 bg-amber-500 px-4 text-sm font-medium text-amber-950 shadow"
    >
      <UserCheck className="h-4 w-4 shrink-0" />
      <span className="truncate">
        Viewing as{" "}
        {email ? (
          <strong data-testid="impersonation-banner-email">{email}</strong>
        ) : (
          "another user"
        )}{" "}
        — actions are performed as this user. Exit signs you out.
      </span>
      <Button
        size="sm"
        variant="outline"
        data-testid="impersonation-exit"
        className="h-6 border-amber-700 bg-amber-100 px-2 text-xs text-amber-950 hover:bg-amber-50"
        onClick={handleExit}
      >
        <LogOut className="mr-1 h-3 w-3" />
        Exit
      </Button>
    </div>
  );
}
